import { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { createAction } from "./redux/actions";
import { v4 } from "uuid";

export const useDataProvider = (dataProviderName, multipart = false, requestedRecordId = null) => {

    const uid = useMemo(() => v4(), []);

    const dispatch = useDispatch();
    const dataProviderActions = useMemo(
        () => createAction(dataProviderName, uid, multipart),
        [dataProviderName, uid, multipart],
    );

    const idItems = (useSelector(state => state[dataProviderName])?.components?.[uid]?.items) ?? [];
    const globalItems = (useSelector(state => state[dataProviderName])?.global?.items) ?? {};
    const items = useMemo(
        () => (Array.isArray(idItems) ? idItems.map(idItem => globalItems[idItem]) : idItems),
        [idItems, globalItems],
    );
    const total = useSelector(state => state[dataProviderName])?.components?.[uid]?.total ?? 0;
    const loading = useSelector(state => state[dataProviderName])?.components?.[uid]?.loading ?? false;
    const downloading = useSelector(state => state[dataProviderName])?.components?.[uid]?.downloading ?? null;
    const saving = useSelector(state => state[dataProviderName])?.components?.[uid]?.saving ?? null;
    const success = useSelector(state => state[dataProviderName])?.components?.[uid]?.success ?? null;
    const globalItem = useSelector(state => state[dataProviderName])?.global?.item ?? null;
    const componentItem = useSelector(state => state[dataProviderName])?.components?.[uid]?.item;
    const item = useMemo(() => {
        const globalItemId = globalItem?.data?._id ?? globalItem?.data?.id;

        if (typeof componentItem === 'string') {
            if (globalItems[componentItem]) {
                return { data: globalItems[componentItem] };
            }
            if (globalItemId === componentItem && globalItem?.data) {
                return globalItem;
            }
            if (requestedRecordId && componentItem === requestedRecordId) {
                return null;
            }
            if (!requestedRecordId && globalItem?.data) {
                return globalItem;
            }
            return null;
        }

        if (componentItem && typeof componentItem === 'object') {
            if (componentItem.data) return componentItem;
            if (componentItem._id || componentItem.id) return { data: componentItem };
        }

        if (requestedRecordId && globalItemId && globalItemId !== requestedRecordId) {
            return null;
        }

        if (globalItem?.data) {
            return globalItem;
        }

        return null;
    }, [globalItems, componentItem, globalItem, requestedRecordId]);

    const errorMessage = (useSelector(state => state[dataProviderName])?.components?.[uid]?.errorMessage) ?? "";
    const errors = (useSelector(state => state[dataProviderName])?.components?.[uid]?.errors) ?? {};
    const reloadCounter = useSelector(state => state.reloadCounter || 0);

    const loadAll = useCallback(
        (filterModel, sortModel, page, per_page, otherFilters, requestOptions) => {
            return dispatch(
                dataProviderActions.getAll(
                    filterModel,
                    sortModel,
                    page,
                    per_page,
                    otherFilters,
                    requestOptions,
                ),
            );
        },
        [dispatch, dataProviderActions],
    );

    const reset = useCallback(() => {
        dispatch(dataProviderActions.reset());
    }, [dispatch, dataProviderActions]);

    const loadOne = useCallback((id, params = []) => {
        return dispatch(dataProviderActions.getOne(id, params));
    }, [dispatch, dataProviderActions]);

    const getOne = useCallback((id) => {
        return globalItems[id] ?? null;
    }, [globalItems]);

    const save = useCallback((values) => {
        return dispatch(dataProviderActions.save(values));
    }, [dispatch, dataProviderActions]);

    const doAction = useCallback((values) => {
        return dispatch(dataProviderActions.doAction(values));
    }, [dispatch, dataProviderActions]);

    const destroy = useCallback((id) => {
        return dispatch(dataProviderActions.destroy(id));
    }, [dispatch, dataProviderActions]);

    const download = useCallback((recipe, data = null) => {
        return dispatch(dataProviderActions.download(recipe, data));
    }, [dispatch, dataProviderActions]);

    const uploadAndDownload = useCallback((values) => {
        return dispatch(dataProviderActions.uploadAndDownload(values));
    }, [dispatch, dataProviderActions]);

    return useMemo(
        () => ({
            uid,
            items,
            total,
            loading,
            saving,
            success,
            item,
            errorMessage,
            errors,
            reloadCounter,
            loadAll,
            reset,
            loadOne,
            getOne,
            save,
            doAction,
            destroy,
            download,
            downloading,
            uploadAndDownload,
        }),
        [
            uid,
            items,
            total,
            loading,
            saving,
            success,
            item,
            errorMessage,
            errors,
            reloadCounter,
            loadAll,
            reset,
            loadOne,
            getOne,
            save,
            doAction,
            destroy,
            download,
            downloading,
            uploadAndDownload,
        ],
    );
}
